import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../../app/session';
import { LanguageSelect } from '../../components/LanguageSelect';
import { BRAND } from '../../config';
import { useT, type DictKey } from '../../i18n';

const ERRORS: Record<string, DictKey> = {
  invalid_credentials: 'login.invalid',
  invalid_totp: 'login.invalidTotp',
  membership_suspended: 'login.suspended',
  no_membership: 'login.noMembership',
  organization_suspended: 'login.orgSuspended',
  rate_limited: 'err.rate_limited',
  network_error: 'err.network',
};

/** Email (or login) + password; the 2FA code and the company choice appear only when the server asks for them. */
export function LoginPage() {
  const t = useT();
  const nav = useNavigate();
  const location = useLocation();
  // back to the screen the session expired on (e.g. a handover opened from a notification)
  const from = (location.state as { from?: string } | null)?.from;
  const login = useSession((s) => s.login);
  const expired = useSession((s) => s.expired);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [totp, setTotp] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<string[] | null>(null);
  const [org, setOrg] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DictKey | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('err.validation');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await login(email, password, totp ?? undefined, org || undefined, remember);
    setBusy(false);
    switch (r.kind) {
      case 'ok':
        nav(from && from.startsWith('/') && from !== '/login' ? from : '/', { replace: true });
        break;
      case 'must_change':
        nav('/password', { replace: true });
        break;
      case 'not_partner':
        nav('/not-partner', { replace: true });
        break;
      case 'totp':
        setTotp('');
        break;
      case 'choose_org':
        setOrgs(r.organizations);
        setOrg(r.organizations[0] ?? '');
        break;
      case 'error':
        setError(ERRORS[r.code] ?? (r.status === 429 ? 'err.rate_limited' : 'err.generic'));
        if (r.code === 'invalid_totp') setTotp('');
        break;
    }
  }

  return (
    <div className="page min-h-[100dvh] pb-10 pt-[max(24px,env(safe-area-inset-top))]">
      <p className="text-[13px] font-semibold uppercase tracking-[.2em] text-lab">{BRAND}</p>
      <h1 className="mt-2">{t('app.name')}</h1>
      <p className="mt-2 text-[15px] text-txt">{t('login.intro')}</p>
      {expired ? (
        <p role="status" className="mt-4 rounded-xl border border-warn/60 px-4 py-3 text-[15px] text-warn">
          {t('login.expired')}
        </p>
      ) : null}

      <form className="mt-6 space-y-4" onSubmit={submit} noValidate>
        <div>
          <label htmlFor="email" className="label">
            {t('login.email')}
          </label>
          <input
            id="email"
            className="input"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="password" className="label">
            {t('login.password')}
          </label>
          <input
            id="password"
            className="input"
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <label className="mt-1 flex min-h-tap items-center gap-3 text-[15px] text-txt">
            <input type="checkbox" className="h-5 w-5" checked={show} onChange={(e) => setShow(e.target.checked)} />
            {t('login.showPassword')}
          </label>
        </div>
        {totp !== null ? (
          <div>
            <label htmlFor="totp" className="label">
              {t('login.totp')}
            </label>
            <input
              id="totp"
              className="input tracking-[.3em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              autoFocus
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/[^\d]/g, ''))}
            />
            <p className="mt-1 text-[14px] text-lab">{t('login.totpHint')}</p>
          </div>
        ) : null}
        {orgs ? (
          <div>
            <label htmlFor="org" className="label">
              {t('login.org')}
            </label>
            <select id="org" className="input" value={org} onChange={(e) => setOrg(e.target.value)}>
              {orgs.map((id, i) => (
                <option key={id} value={id}>
                  {t('login.orgItem', { n: i + 1 })} · {id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <label className="flex min-h-tap items-center gap-3 text-[15px]">
          <input type="checkbox" className="h-6 w-6" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          {t('login.remember')}
        </label>
        {error ? (
          <p role="alert" className="rounded-xl border border-bad/50 px-4 py-3 text-bad">
            {t(error)}
          </p>
        ) : null}
        <button type="submit" className="btn-primary min-h-big w-full text-[18px]" disabled={busy}>
          {busy ? t('login.busy') : t('login.submit')}
        </button>
      </form>

      <div className="mt-8">
        <LanguageSelect id="login-lang" />
      </div>
    </div>
  );
}
